import { AssessmentAnswer, AssessmentTypePropertySet } from '../@types/common.types';
import { AssessmentType, Gender } from '../enums/shared.enums';
import { AssessmentSeverityKey } from '../theme/palette';

export const AssessmentTypeQuestionHeaders = {
    [AssessmentType.PHQ9]: 'Over the last 2 weeks, how often have you been bothered by...',
    [AssessmentType.GAD7]: 'Over the last 2 weeks, how often have you been bothered by...',
    [AssessmentType.VC_FEEDBACK]: 'Please provide feedback about your video session experience:',
    [AssessmentType.PROVIDER_REVIEW]: 'Please provide feedback about your provider',
    [AssessmentType.AUDIT_C]: 'In the past 12 months...',
    [AssessmentType.DAST_10]: 'In the past 12 months...',
    [AssessmentType.SIP_AD_5]:
        'In the past 3 months, how often has your drinking or drug use caused the following?',
};

export const AssessmentTypeAnswers = {
    [AssessmentType.PHQ9]: [
        {
            value: 0,
            label: 'Not at all',
        },
        {
            value: 1,
            label: 'Several days',
        },
        {
            value: 2,
            label: 'More than half the days',
        },
        {
            value: 3,
            label: 'Nearly every day',
        },
    ] as AssessmentAnswer[],
    [AssessmentType.GAD7]: [
        {
            value: 0,
            label: 'Not at all',
        },
        {
            value: 1,
            label: 'Several days',
        },
        {
            value: 2,
            label: 'More than half the days',
        },
        {
            value: 3,
            label: 'Nearly every day',
        },
    ] as AssessmentAnswer[],
    [AssessmentType.VC_FEEDBACK]: [
        {
            value: 1,
            label: 'Yes, I experienced audio or video issues',
        },
        {
            value: 0,
            label: 'No, everything worked well',
        },
    ] as AssessmentAnswer[],
    [AssessmentType.VC_FEEDBACK + '_Q2']: [
        {
            value: 0,
            label: 'No issues',
        },
        {
            value: 1,
            label: 'Audio issues',
        },
        {
            value: 2,
            label: 'Video issues',
        },
        {
            value: 3,
            label: 'Both audio and video issues',
        },
        {
            value: 4,
            label: 'Other',
        },
    ] as AssessmentAnswer[],
    [AssessmentType.PROVIDER_REVIEW]: [
        {
            value: 0,
            label: 'maximum',
        },
        {
            value: 1,
            label: 'minimum',
        },
    ],
    [AssessmentType.PROVIDER_REVIEW + '_Q2']: [
        {
            value: 1,
            label: 'yes',
        },
        {
            value: 0,
            label: 'no',
        },
    ],
    [AssessmentType.AUDIT_C]: [
        {
            value: 0,
            label: 'Never',
        },
        {
            value: 1,
            label: 'Monthly or less',
        },
        {
            value: 2,
            label: '2-4 times a month',
        },
        {
            value: 3,
            label: '2-3 times a week',
        },
        {
            value: 4,
            label: '4+ times a week',
        },
    ],
    [AssessmentType.AUDIT_C + '_Q2']: [
        {
            value: 0,
            label: '1-2',
        },
        {
            value: 1,
            label: '3-4',
        },
        {
            value: 2,
            label: '5-6',
        },
        {
            value: 3,
            label: '7-9',
        },
        {
            value: 4,
            label: '10+',
        },
    ],
    [AssessmentType.AUDIT_C + '_Q3']: [
        {
            value: 0,
            label: 'Never',
        },
        {
            value: 1,
            label: 'Less than monthly',
        },
        {
            value: 2,
            label: 'Monthly',
        },
        {
            value: 3,
            label: 'Weekly',
        },
        {
            value: 4,
            label: 'Daily or almost daily',
        },
    ],
    [AssessmentType.DAST_10]: [
        {
            value: 1,
            label: 'Yes',
        },
        {
            value: 0,
            label: 'No',
        },
    ],
    [AssessmentType.SIP_AD_5]: [
        {
            value: 0,
            label: 'Never',
        },
        {
            value: 1,
            label: 'Once or a few times',
        },
        {
            value: 2,
            label: 'Weekly',
        },
        {
            value: 3,
            label: 'Daily or almost daily',
        },
    ],
};

//
// const COLORS = ['rgb(186,229,224)', 'rgb(148,171,239)', 'rgb(1,159,139)'];

export const AssessmentTypeProperties = {
    [AssessmentType.PHQ9]: {
        active: true,
        fullName: 'Patient Health Questionnaire 9',
        description:
            'This is a 9-item self-administered tool for screening, diagnosing, monitoring, and measuring the severity of depression.',
        measurement: 'depression',
        maxTotalScore: 27,
        maxQuestionScore: 3,
        numQuestions: 9,
        questions: [
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Little interest or pleasure in doing things',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Feeling down, depressed, or hopeless',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Trouble falling or staying asleep, or sleeping too much',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Feeling tired or having little energy',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Poor appetite or overeating',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Feeling bad about yourself - or that you are a failure or have let yourself or your family down',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Trouble concentrating on things, such as reading the newspaper or watching television',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PHQ9],
                text: 'Thoughts that you would be better off dead, or of hurting yourself in some way',
                answerSet: AssessmentTypeAnswers[AssessmentType.PHQ9],
            },
        ],
        interpretation: (score: number) => {
            if (score <= 4) {
                return 'minimal';
            } else if (score <= 9) {
                return 'mild';
            } else if (score <= 14) {
                return 'moderate';
            } else if (score <= 19) {
                return 'moderately severe';
            } else {
                return 'severe';
            }
        },
    } as AssessmentTypePropertySet,
    [AssessmentType.GAD7]: {
        active: true,
        fullName: 'Generalized Anxiety Disorder 7',
        description:
            'This is a 7-item scale for measuring severity of generalized anxiety disorder.',
        measurement: 'anxiety',
        maxTotalScore: 21,
        maxQuestionScore: 3,
        numQuestions: 7,
        questions: [
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Feeling nervous, anxious, or on edge',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Not being able to stop or control worrying',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Worrying too much about different things',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Trouble relaxing',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Being so restless that it is hard to sit still',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Becoming easily annoyed or irritable',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.GAD7],
                text: 'Feeling afraid as if something awful might happen',
                answerSet: AssessmentTypeAnswers[AssessmentType.GAD7],
            },
        ],
        interpretation: (score: number) => {
            if (score <= 4) {
                return 'minimal';
            } else if (score <= 9) {
                return 'mild';
            } else if (score <= 14) {
                return 'moderate';
            } else {
                return 'severe';
            }
        },
    } as AssessmentTypePropertySet,

    [AssessmentType.PROMIS_ANGER]: {} as AssessmentTypePropertySet,
    [AssessmentType.PROMIS_SLEEP]: {} as AssessmentTypePropertySet,
    [AssessmentType.AWARE]: {} as AssessmentTypePropertySet,
    [AssessmentType.VC_FEEDBACK]: {
        active: false,
        fullName: 'Video Call Feedback',
        description:
            'This is a brief questionnaire to gather feedback about your video session experience.',
        measurement: 'feedback',
        maxTotalScore: 6,
        maxQuestionScore: 4,
        numQuestions: 2,
        questions: [
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.VC_FEEDBACK],
                text: 'Did you experience any audio or video issues during this session?',
                answerSet: AssessmentTypeAnswers[AssessmentType.VC_FEEDBACK],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.VC_FEEDBACK],
                text: 'What problems did you experience?',
                answerSet: AssessmentTypeAnswers[AssessmentType.VC_FEEDBACK + '_Q2'],
            },
        ],
        interpretation: (score: number) => {
            if (score <= 1) {
                return 'severe';
            } else if (score <= 2) {
                return 'moderately severe';
            } else if (score <= 3) {
                return 'moderate';
            } else if (score <= 4) {
                return 'mild';
            } else {
                return 'minimal';
            }
        },
    } as AssessmentTypePropertySet,
    [AssessmentType.PROVIDER_REVIEW]: {
        active: true,
        fullName: 'Provider Review',
        description: 'This is a 2-item review for your provider.',
        measurement: 'performance',
        maxTotalScore: 11,
        maxQuestionScore: 11,
        numQuestions: 2,
        questions: [
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PROVIDER_REVIEW],
                text: 'How would you rate your provider',
                answerSet: AssessmentTypeAnswers[AssessmentType.PROVIDER_REVIEW],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.PROVIDER_REVIEW],
                text: 'Would you like to change providers?',
                answerSet: AssessmentTypeAnswers[AssessmentType.PROVIDER_REVIEW + '_Q2'],
            },
        ],
        interpretation: (_score: number) => {
            return 'minimal';
        },
    } as AssessmentTypePropertySet,
    [AssessmentType.AUDIT_C]: (() => {
        const prop: AssessmentTypePropertySet = {
            active: true,
            fullName: 'Substance Use Assessment: Alcohol Use',
            description:
                'This is a 3-item alcohol use screening that helps identify hazardous drinking or potential alcohol use disorder.',
            measurement: 'hazardous drinking',
            maxTotalScore: 12,
            maxQuestionScore: 4,
            numQuestions: 3,
            questions: [
                {
                    header: AssessmentTypeQuestionHeaders[AssessmentType.AUDIT_C],
                    text: 'How often do you have a drink containing alcohol?',
                    answerSet: AssessmentTypeAnswers[AssessmentType.AUDIT_C],
                },
                {
                    header: AssessmentTypeQuestionHeaders[AssessmentType.AUDIT_C],
                    text: 'How many standard drinks containing alcohol do you have on a typical day when drinking?',
                    answerSet: AssessmentTypeAnswers[AssessmentType.AUDIT_C + '_Q2'],
                },
                {
                    header: AssessmentTypeQuestionHeaders[AssessmentType.AUDIT_C],
                    text: 'How often do you have six or more drinks on one occasion?',
                    answerSet: AssessmentTypeAnswers[AssessmentType.AUDIT_C + '_Q3'],
                },
            ],
            interpretation: (score: number) => {
                switch (true) {
                    case prop.context?.gender == Gender.MALE && score >= 4:
                        return 'severe';
                    case prop.context?.gender == Gender.FEMALE && score >= 3:
                        return 'severe';
                    default:
                        return 'minimal';
                }
            },
        };

        prop.setContext = (context) => {
            prop.context = context;
        };

        return prop;
    })(),
    [AssessmentType.DAST_10]: {
        active: true,
        fullName: 'Substance Use Assessment: Drug Use (last 12 months)',
        description:
            'This is a 10-item screening that evaluates behaviors related to drug use and abuse.',
        measurement: 'drug use',
        maxTotalScore: 10,
        maxQuestionScore: 1,
        numQuestions: 10,
        questions: [
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Have you used drugs other than those required for medical reasons?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Do you abuse more than one drug at a time?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Are you always able to stop using drugs when you want to?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Have you had “blackouts” or “flashbacks” as a result of drug use?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Do you ever feel bad or guilty about your drug use?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Does your spouse/parent/partner ever complain about your involvement with drugs?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Have you neglected family, work, or school because of drugs?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Have you engaged in illegal activities to obtain drugs?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Have you experienced withdrawal symptoms (felt sick) when you stopped taking drugs?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.DAST_10],
                text: 'Have you had medical problems as a result of your drug use?',
                answerSet: AssessmentTypeAnswers[AssessmentType.DAST_10],
            },
        ],
        interpretation: (score: number) => {
            switch (true) {
                case score == 0:
                    return 'no';
                case score <= 2:
                    return 'minimal';
                case score <= 5:
                    return 'moderate';
                case score <= 8:
                    return 'moderately severe';
                default:
                    return 'severe';
            }
        },
    } as AssessmentTypePropertySet,
    [AssessmentType.SIP_AD_5]: {
        active: true,
        fullName: 'Substance Use Assessment: Consequences of Use (last 3 months)',
        description:
            'This is a 5-item screening that assesses negative consequences associated with alcohol and drug use.',
        measurement: 'substance abuse',
        maxTotalScore: 15,
        maxQuestionScore: 3,
        numQuestions: 5,
        questions: [
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.SIP_AD_5],
                text: 'I have been unhappy because of my drinking or drug use. (Intrapersonal)',
                answerSet: AssessmentTypeAnswers[AssessmentType.SIP_AD_5],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.SIP_AD_5],
                text: 'A friendship or close relationship has been damaged by my drinking or drug use. (Interpersonal)',
                answerSet: AssessmentTypeAnswers[AssessmentType.SIP_AD_5],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.SIP_AD_5],
                text: 'My drinking or drug use has interfered with my work or school performance. (Work/Functioning)',
                answerSet: AssessmentTypeAnswers[AssessmentType.SIP_AD_5],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.SIP_AD_5],
                text: 'I have taken foolish risks when I have been drinking or using drugs. (Impulse control)',
                answerSet: AssessmentTypeAnswers[AssessmentType.SIP_AD_5],
            },
            {
                header: AssessmentTypeQuestionHeaders[AssessmentType.SIP_AD_5],
                text: 'I have had medical or health problems caused by my drinking or drug use. (Physical)',
                answerSet: AssessmentTypeAnswers[AssessmentType.SIP_AD_5],
            },
        ],
        interpretation: (score: number) => {
            switch (true) {
                case score <= 3:
                    return 'minimal';
                case score <= 8:
                    return 'moderate';
                default:
                    return 'severe';
            }
        },
    } as AssessmentTypePropertySet,
};

export type Severity = AssessmentSeverityKey;

// Severity swatches live in src/theme/palette.ts (getAssessmentSeverityColors) so they can
// resolve per color mode while staying WCAG AA compliant.
export { getAssessmentSeverityColors } from '../theme/palette';
export type { AssessmentSeverityColor, AssessmentSeverityColorScheme } from '../theme/palette';

export const VIEWABLE_ASSESSMENTS = [AssessmentType.PHQ9, AssessmentType.GAD7];

export const EAP_VIEWABLE_ASSESSMENTS = [
    AssessmentType.AUDIT_C,
    AssessmentType.DAST_10,
    AssessmentType.SIP_AD_5,
];
